import { useAudio, QueueableArticle } from '../contexts/AudioContext'

interface Props {
  article: QueueableArticle
  variant?: 'icon' | 'button' | 'full'
}

export function ListenButton({ article, variant = 'button' }: Props) {
  const { currentItem, queue, addToQueue, playArticle, isPlaying } = useAudio()

  const isCurrentlyPlaying = currentItem?.articleId === article.id
  const isInQueue = queue.some(item => item.articleId === article.id)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent click handlers

    if (isCurrentlyPlaying || isInQueue) return

    // If nothing playing, play immediately
    if (!currentItem && queue.length === 0) {
      playArticle(article)
    } else {
      addToQueue(article)
    }
  }

  if (variant === 'icon') {
    return (
      <button
        className={`listen-btn-icon ${isInQueue ? 'queued' : ''} ${isCurrentlyPlaying ? 'playing' : ''}`}
        onClick={handleClick}
        disabled={isCurrentlyPlaying}
        title={isCurrentlyPlaying ? 'Playing' : isInQueue ? 'In queue' : 'Listen'}
      >
        {isCurrentlyPlaying && isPlaying ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="playing-icon">
            <rect x="4" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;8;16" dur="0.8s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;8;4" dur="0.8s" repeatCount="indefinite" />
            </rect>
            <rect x="10.5" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;12;16" dur="0.6s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;6;4" dur="0.6s" repeatCount="indefinite" />
            </rect>
            <rect x="17" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;10;16" dur="0.7s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;7;4" dur="0.7s" repeatCount="indefinite" />
            </rect>
          </svg>
        ) : isInQueue ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 18v-6a9 9 0 0118 0v6" />
            <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z" />
          </svg>
        )}
      </button>
    )
  }

  if (variant === 'full') {
    return (
      <button
        className={`listen-btn-full ${isInQueue ? 'queued' : ''} ${isCurrentlyPlaying ? 'playing' : ''}`}
        onClick={handleClick}
        disabled={isCurrentlyPlaying}
      >
        {isCurrentlyPlaying ? (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="playing-icon">
              <rect x="4" y="4" width="3" height="16" rx="1">
                <animate attributeName="height" values="16;8;16" dur="0.8s" repeatCount="indefinite" />
                <animate attributeName="y" values="4;8;4" dur="0.8s" repeatCount="indefinite" />
              </rect>
              <rect x="10.5" y="4" width="3" height="16" rx="1">
                <animate attributeName="height" values="16;12;16" dur="0.6s" repeatCount="indefinite" />
                <animate attributeName="y" values="4;6;4" dur="0.6s" repeatCount="indefinite" />
              </rect>
              <rect x="17" y="4" width="3" height="16" rx="1">
                <animate attributeName="height" values="16;10;16" dur="0.7s" repeatCount="indefinite" />
                <animate attributeName="y" values="4;7;4" dur="0.7s" repeatCount="indefinite" />
              </rect>
            </svg>
            Playing
          </>
        ) : isInQueue ? (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            In Queue
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 18v-6a9 9 0 0118 0v6" />
              <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z" />
            </svg>
            Listen
          </>
        )}
      </button>
    )
  }

  // Default 'button' variant
  return (
    <button
      className={`listen-btn ${isInQueue ? 'queued' : ''} ${isCurrentlyPlaying ? 'playing' : ''}`}
      onClick={handleClick}
      disabled={isCurrentlyPlaying}
    >
      {isCurrentlyPlaying ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="playing-icon">
            <rect x="4" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;8;16" dur="0.8s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;8;4" dur="0.8s" repeatCount="indefinite" />
            </rect>
            <rect x="10.5" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;12;16" dur="0.6s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;6;4" dur="0.6s" repeatCount="indefinite" />
            </rect>
            <rect x="17" y="4" width="3" height="16" rx="1">
              <animate attributeName="height" values="16;10;16" dur="0.7s" repeatCount="indefinite" />
              <animate attributeName="y" values="4;7;4" dur="0.7s" repeatCount="indefinite" />
            </rect>
          </svg>
          Playing
        </>
      ) : isInQueue ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Queued
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 18v-6a9 9 0 0118 0v6" />
            <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z" />
          </svg>
          Listen
        </>
      )}
    </button>
  )
}
